import {
  ColorPaletteStyles,
  DEFAULT_LIGHT_THEME,
  PaletteTextType,
  Spacing,
  TextVariantWithModifiers,
} from "@assets/style/appMuiTheme"
import utilClasses from "@assets/style/utilities.module.css"
import { darken, getContrastRatio, lighten, Theme } from "@material-ui/core"
import { Palette } from "@material-ui/core/styles/createPalette"
import { CSSProperties } from "react"

type PaletteTextTypeKind = keyof PaletteTextType
type GroovyPaletteKind = keyof Palette["groovy"]
type GroovyPaletteColorKind = "primary" | "warning" | "error" | "success"
type GroovyColorKey = keyof ColorPaletteStyles
type PaletteColorKind = "main" | "light" | "dark"
export type GroovyTextColorKind =
  | `groovy.${GroovyPaletteKind}.${GroovyColorKey}`
  | `${GroovyPaletteColorKind}.${PaletteColorKind}`
  | `text.${PaletteTextTypeKind}`
  | "common.white"
  | "common.black"
  | "primary.contrastText"

export type GroovyBorderRadiusUnits = 2 | 4 | 6 | 8 | 12 | 16 | 20 | 24

/**
 * Map `groovy.blue.300` ->  `var(--groovy-blue-300)`
 */
export function mapColorToCSSVariable(color: GroovyTextColorKind): string {
  const colorParts = color.split(".")

  // Check to see if the color has at least two parts
  if (colorParts.length < 2) return ""

  const category = colorParts[0]
  const palette = colorParts[1]

  if (colorParts.length === 2) {
    switch (category) {
      case "common":
        return `var(--groovy-common-${palette})`
      case "text":
        return `var(--groovy-text-${palette})`
      case "warning":
      case "error":
      case "sucess":
        return `var(--groovy-${category}-${palette})`
      case "primary":
        // "contrastText" is a special case since it has 4 parts
        if (palette === "contrastText") {
          return `var(--groovy-primary-contrast-text)`
        }
        return `var(--groovy-primary-${palette})`
    }
  }

  if (colorParts.length === 3 && category === "groovy") {
    const key = colorParts[2]
    // Special case for onDark palette
    if (palette === "onDark") {
      return `var(--groovy-on-dark-${key})`
    }

    return `var(--groovy-${palette}-${key})`
  }

  return ""
}

/**
 * Split the text variant into variant type, size, weight and text transfrom (optional)
 */
export function parseTextVariant(variant: TextVariantWithModifiers): {
  variantType: string
  weight: string
  textTransform: Exclude<CSSProperties["textTransform"], undefined>
} {
  const [t, s, wOrTs, ts] = variant.split("-")
  const isTextTransform = wOrTs === "uppercase"
  let weight
  if (wOrTs && !isTextTransform) {
    weight = wOrTs
  } else {
    // Headings get 700 weight by default, body text gets 400
    weight = t === "heading" ? "700" : "400"
  }

  return {
    variantType: [t, s].join("-"),
    weight,
    textTransform: isTextTransform
      ? wOrTs
      : (ts as CSSProperties["textTransform"]) || "initial",
  }
}

export function spacingVars(s: Spacing): CSSProperties {
  const {
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    padding,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
  } = s

  return {
    "--mt": marginTop ?? margin ?? 0,
    "--mr": marginRight ?? margin ?? 0,
    "--mb": marginBottom ?? margin ?? 0,
    "--ml": marginLeft ?? margin ?? 0,
    "--pt": paddingTop ?? padding ?? 0,
    "--pr": paddingRight ?? padding ?? 0,
    "--pb": paddingBottom ?? padding ?? 0,
    "--pl": paddingLeft ?? padding ?? 0,
  } as CSSProperties
}

export function spacingClass() {
  return utilClasses.spacing
}

export function extractRestFromSpacing<T extends Spacing>(props: T) {
  const propsCopy = { ...props }
  for (const spacingKey in [
    "margin",
    "marginTop",
    "marginBottom",
    "marginLeft",
    "marginRight",
    "padding",
    "paddingTop",
    "paddingBottom",
    "paddingLeft",
    "paddingRight",
  ]) {
    if (propsCopy[spacingKey as keyof T] !== undefined) {
      delete propsCopy[spacingKey as keyof T]
    }
  }
  return propsCopy as Omit<T, keyof Spacing>
}

export function truncateVars(lines: number | undefined) {
  return {
    // truncate-x-lines util relies on this CSS variable to work
    "--truncate-x-lines": lines && lines > 1 ? lines : undefined,
  }
}

export function truncateClass(lines: number | undefined) {
  if (!lines || lines < 1) return
  if (lines === 1) return utilClasses.truncateLine
  return utilClasses.truncateMultipleLines
}

/** Compute the theme variables affected by custom Theme */
export function createCustomThemeCSSVariables(
  theme?: {
    cardBackgroundColor?: string | null
    primaryColor?: string | null
    primaryContrastColor?: string | null
    backgroundColor?: string | null
    navBackgroundColor?: string | null
  } | null
): CSSProperties {
  const { primaryContrastColor, backgroundColor, navBackgroundColor } =
    theme ?? DEFAULT_LIGHT_THEME

  const lightPaperColor = "#FFFFFF"
  const paperColor = theme?.cardBackgroundColor ?? DEFAULT_LIGHT_THEME.cardBackgroundColor
  const primaryColor = theme?.primaryColor ?? DEFAULT_LIGHT_THEME.primaryColor
  const isDark = getContrastRatio(lightPaperColor, paperColor) > 1.5
  const isDefaultLightPaperColor = paperColor === lightPaperColor

  /** Note: This theme object is incomplete, it only contains the necessary variables used by
   *   the converted JSS style components
   */
  return {
    "--groovy-primary-main": primaryColor,
    "--groovy-primary-light": lighten(primaryColor, 0.9),
    "--groovy-primary-hover": lighten(primaryColor, 0.1),
    "--groovy-primary-contrast-text": primaryContrastColor || "#FFFFFF",

    "--groovy-background-main": backgroundColor || "#FCFCFC",
    "--groovy-background-paper": paperColor,
    "--groovy-background-nav": navBackgroundColor || "#F4F5F7",

    "--groovy-depths-inside-card": isDark
      ? `0px 1px 4px 0px ${darken(paperColor, 0.55)}`
      : `0px 1px 4px 0px ${darken(paperColor, 0.15)}`,
    "--groovy-depths-box-shadow": isDefaultLightPaperColor
      ? "rgb(46 64 88 / 4%) 0px 2px 4px, rgb(46 64 88 / 8%) 0px 6px 18px"
      : isDark
      ? `0px 2px 4px ${darken(paperColor, 0.44)}, 0px 6px 18px ${darken(
          paperColor,
          0.48
        )}`
      : `0px 2px 4px ${darken(paperColor, 0.04)}, 0px 6px 18px ${darken(
          paperColor,
          0.08
        )}`,
    "--groovy-depths-raised-box-shadow": isDefaultLightPaperColor
      ? "0px 4px 8px rgba(46, 64, 88, 0.08), 0px 16px 32px rgba(46, 64, 88, 0.1)"
      : isDark
      ? `0px 4px 8px ${darken(paperColor, 0.53)}, 0px 16px 32px ${darken(
          paperColor,
          0.55
        )}`
      : `0px 4px 8px ${darken(paperColor, 0.08)}, 0px 16px 32px ${darken(
          paperColor,
          0.1
        )}`,
    "--groovy-depths-xs": isDark ? "none" : "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    "--groovy-depths-ai-shadow": isDark
      ? "none"
      : "0px 4px 20px 0px rgba(68, 103, 246, 0.30)",
  } as CSSProperties
}

/** Compute the theme variables affected by custom Theme */
export function createThemeCSSVariablesfromJss(theme: Theme): CSSProperties {
  return createCustomThemeCSSVariables({
    cardBackgroundColor: theme?.palette.background.paper,
    primaryColor: theme?.palette.primary.main,
    primaryContrastColor: theme?.palette.primary.contrastText,
    backgroundColor: theme?.palette.background.default,
    navBackgroundColor: theme?.palette.background.nav,
  })
}

/** Helper function to create CSS variables without casting it to CSSProperties */
export function css(cssDeclarations: {
  [key: string]: string | number | undefined
}): CSSProperties {
  return cssDeclarations as CSSProperties
}

/** Convert value from JS to CSS */
export function cssSafeValue(value: string | number | undefined): string | undefined {
  // If the value is a number, append px to the value
  if (typeof value === "number") {
    return `${value}px`
  }

  return value
}
